import React, { useState } from 'react';
import styled from '@emotion/styled';
import ErrorMessage from 'components/common/WarningInputMessage/WarningInputMessage';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useTranslation } from 'react-i18next';

type Props = React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
> & {
    padding?: string;
    dangerColor?: boolean;
    errorMsg?: string;
    width?: string;
    margin?: string;
    allowOnlyIntegers?: boolean;
};

export const StyledTableInput = styled.input<Props>`
    border: 1px solid ${(props) => (props.errorMsg ? '#FF2727' : '#e7e7e7')};
    border-radius: 3px;
    padding: ${(props) => (props.padding ? props.padding : '7px 14px')};
    width: ${(props) => (props.width ? props.width : '100%')};
    max-width: 100%;
    font-family: Assistant, sans-serif;
    font-size: 15px;
    line-height: 20px;
    color: ${(props) => (props.dangerColor ? '#FF2727' : '#282828')};
    &:hover,
    &:active,
    &:focus-visible {
        border-color: ${(props) => (props.errorMsg ? '#FF2727' : '#e7e7e7')};
        outline: none;
    }
`;

const Wrapper = styled.div<Props>`
    width: ${(props) => (props.width ? props.width : '100%')};
    margin: ${(props) => (props.margin ? props.margin : '0')};
    max-width: 100%;
    position: relative;
`;

const StyledSpan = styled.span`
    color: #ff2727;
    font-size: 11px;
    line-height: 11px;
    position: absolute;
    bottom: -2px;
    transform: translateY(100%);
    white-space: nowrap;
    left: 0;
`;

const StyledTableInputWithError = ({
    errorMsg,
    width,
    dangerColor,
    padding,
    margin,
    allowOnlyIntegers,
    ...rest
}: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [inputError, setInputError] = useState<string>('');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let { value } = event.target;
        setInputError(
            /^\d*$/.test(value) ? '' : `${t('pleaseEnterNumericValues', { lng: language })}`
        );
        if (allowOnlyIntegers) {
            value = value.replace(/\D/g, '');
            event.target.value = value;
        }
        rest.onChange && rest.onChange(event);
    };

    return (
        <Wrapper width={width} margin={margin}>
            <StyledTableInput
                {...rest}
                errorMsg={errorMsg || inputError}
                dangerColor={dangerColor}
                padding={padding}
                onChange={allowOnlyIntegers ? handleChange : rest.onChange}
            />
            {(errorMsg || inputError) && (
                <StyledSpan>
                    <ErrorMessage message={errorMsg || inputError} />
                </StyledSpan>
            )}
        </Wrapper>
    );
};

export default StyledTableInputWithError;
